import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import * as api from '../api.ts';
import { useCountdown } from '../countdown.ts';
import { BuildingIcon, ExitIcon, LockIcon } from '../icons.tsx';
import { useMapCanvas, useMapRegion } from '../map-layer.tsx';
import type { MapCircle, MapPoint, MapPolygon } from '../map-style.ts';
import { frameFor } from '../lib/frame.ts';
import { wanderedFrom } from '../lib/camera-return.ts';
import { PROCEDURES, resolveStoredInstruction, type Procedure } from '../lib/procedures.ts';
import { Button, Finding, InlineAction } from '../ui.tsx';
import {
  Chevron,
  FIELD,
  FieldInput,
  FieldLabel,
  FieldSelect,
  FieldStatic,
  Help,
  Segmented,
  Switch,
} from '../lib/form.tsx';

/**
 * Raise an alert — a glass panel over the app's one map.
 *
 * The only screen in the product whose primary action is
 * destructive-by-consequence. Two rules it obeys that no other screen does:
 *
 * **It states what is about to happen in words**, with a count that comes from
 * the same question the send will ask (the server's audience preview, never a
 * client-side roster count).
 *
 * **One dominant action, 64px, and nothing competes with it.** Under acute
 * stress working memory shrinks and vision tunnels; a screen with two equally
 * weighted buttons is a screen where the wrong one gets pressed.
 *
 * The panel slides out from the rail over the left half of the screen (the
 * building wizard's pattern); the right half is the substrate map, drawn with
 * the chosen buildings, their boundaries and the points this procedure sends
 * people to. A point can be turned off here — on the row or by tapping its pin
 * — and it is closed from the moment the alert goes out, redirecting to the
 * point that stays open (the server refuses to close the last one).
 *
 * Several buildings can be raised at once. An event belongs to one building
 * (one notifying event per site), so the panel raises one per building in
 * turn and reports each outcome on its own: a building whose alert went out
 * is never hidden behind one whose alert was refused.
 *
 * Drill or real is chosen before the words are written, as two radios rather
 * than a select — the one choice on this screen that must never be made by
 * accident. The mode is stated three times: the choice, the sentence above the
 * button, and the button's own words. Silent is deliberately not offered: it
 * follows the procedure on the server, because nobody configures it in advance.
 */

/** Which kind of point a procedure sends people to — the server's own mapping (migration 0030). */
function destinationKind(p: Procedure): api.AssemblyKind | null {
  switch (p) {
    case 'evacuation':
      return 'outdoor_assembly';
    case 'lockdown':
      return 'indoor_shelter';
    // An invacuation says "come inside"; it names no point (owner, 2026-10-13).
    default:
      return null;
  }
}

/**
 * The three procedures that send people somewhere. Communication (information
 * only, nobody moves) is deliberately not offered here — it is not an alert in
 * the sense this panel raises, and a fourth button made the three harder to
 * tell apart (owner, 2026-10-13).
 */
const PROCEDURE_CARDS: {
  value: Procedure;
  label: string;
  why: string;
  glyph: (size: number) => ReactNode;
}[] = [
  {
    value: 'evacuation',
    label: 'Evacuation',
    why: 'Everyone leaves the building.',
    glyph: (size) => <ExitIcon size={size} />,
  },
  {
    value: 'lockdown',
    label: 'Lockdown',
    why: 'Lock the door, stay hidden, silent.',
    glyph: (size) => <LockIcon size={size} />,
  },
  {
    value: 'invacuation',
    label: 'Invacuation',
    why: 'Everyone comes inside.',
    glyph: (size) => <BuildingIcon size={size} />,
  },
];

/** What one building's raise came back as. */
type Outcome =
  | {
      siteId: string;
      ok: true;
      res: NonNullable<Awaited<ReturnType<typeof api.raiseEvent>>>;
    }
  | { siteId: string; ok: false; message: string; runningEventId: string | null };

/** The area picker's first row: the whole building, which is what the CAP area says when no zone is picked. */
const WHOLE_SITE = '__whole_site__';

/** How long the operator's own pan or zoom stands before the camera returns to the alert's area. */
const RETURN_AFTER_MS = 8000;

export function Activate({
  orgId,
  sites,
  siteId,
  onSite,
  onLive,
  onClose,
  onOpenBuilding,
}: {
  orgId: string;
  sites: api.Site[];
  siteId: string;
  onSite: (id: string) => void;
  onLive: (eventId: string) => void;
  /** Leave the panel without sending — the rail's Home, by default. */
  onClose?: () => void;
  /** Open a building's page — where its points are added. */
  onOpenBuilding?: (siteId: string) => void;
}) {
  const [mode, setMode] = useState<'drill' | 'live'>('drill');
  const [procedure, setProcedure] = useState<Procedure>('evacuation');
  /**
   * Who is told. Defaults to everyone — all staff on and off site, anyone
   * checked in, and anyone following the building (owner, 2026-10-13); the
   * narrower choices are staff only, and everyone on site without followers.
   * `cross_org` is not offered: nothing crosses an organisation boundary from
   * this panel.
   */
  const [scope, setScope] = useState<api.AudienceScope>('site_all');
  const [instruction, setInstruction] = useState(PROCEDURES[0]!.instruction);
  /**
   * Whether the operator has written their own words. Changing the procedure
   * or the building swaps the suggested instruction, and it must never do that
   * over something a person typed — during an incident the sentence in that
   * box may be the only thing anyone knows about what is happening.
   */
  const [edited, setEdited] = useState(false);
  /**
   * Which part of each building the alert is about — zone ids per building,
   * chosen from the zones the building has set up (owner, 2026-10-13). Nothing
   * chosen means the whole site, which is what the CAP area description then
   * says; chosen zones are named in it ("Level 1, Level 2").
   */
  const [areas, setAreas] = useState<Record<string, string[]>>({});
  /**
   * The buildings this alert is raised on. An organisation with one building
   * starts with it chosen; one with several starts with NONE chosen, so the
   * operator opens the list and picks — a pre-filled building in a 13-building
   * estate is the one that gets alerted by mistake (owner, 2026-10-13).
   */
  const [selected, setSelected] = useState<string[]>(() =>
    sites.length === 1 ? [sites[0]!.id] : [],
  );
  /** Points turned off for this alert, across every selected building. */
  const [closed, setClosed] = useState<Set<string>>(() => new Set());
  const [error, setError] = useState<string | null>(null);
  const [blockedBy, setBlockedBy] = useState<string | null>(null);
  const [awaiting, setAwaiting] = useState<string | null>(null);
  const [authorisers, setAuthorisers] = useState<string[]>([]);
  const [retracting, setRetracting] = useState<{
    eventId: string;
    deadlineIso: string;
    audienceLabel: string;
  } | null>(null);
  /** Per-building outcomes, when more than one building was raised. */
  const [outcomes, setOutcomes] = useState<Outcome[] | null>(null);

  // The shell's site list arrives after this panel can mount; a one-building
  // organisation is pre-filled when it does. Several buildings stay unchosen.
  useEffect(() => {
    if (selected.length > 0 || sites.length !== 1) return;
    setSelected([sites[0]!.id]);
  }, [sites, selected.length]);

  const primaryId = selected[0] ?? '';
  const primary = sites.find((s) => s.id === primaryId);
  const selectedSites = selected
    .map((id) => sites.find((s) => s.id === id))
    .filter((s): s is api.Site => Boolean(s));

  // Keep the shell's building focus on the first chosen building, so leaving
  // the panel lands where the operator was looking.
  useEffect(() => {
    if (primaryId && primaryId !== siteId) onSite(primaryId);
    // onSite is a plain setter in the shell; re-running on its identity would
    // loop a parent re-render back into this effect.
  }, [primaryId]);

  /**
   * The building's own stored instructions (migration 0093). When the primary
   * building has written its own words for a procedure, those become the
   * default the raise flow lands on; otherwise the built-in default does. It
   * never overwrites words the admin has typed — `edited` gates that.
   */
  const storedQueries = useQueries({
    queries: selected.map((id) => ({
      queryKey: ['siteProcedures', orgId, id],
      queryFn: () => api.siteProcedures(orgId, id),
    })),
  });
  const storedProcedures = storedQueries[0];
  const storedData = storedProcedures?.data;
  const resolveDefault = (p: Procedure): string =>
    resolveStoredInstruction(p, storedData?.procedures);
  useEffect(() => {
    if (edited) return;
    setInstruction(resolveDefault(procedure));
    // `edited` is read, not a dependency — flipping it must not re-run and
    // clobber the admin's words.
  }, [storedData, procedure, primaryId]);
  const storedRow = storedData?.procedures.find((p) => p.procedure === procedure);
  const hasStored =
    typeof storedRow?.instruction === 'string' && storedRow.instruction.trim() !== '';
  /**
   * The words building i is sent. Until the admin types, every building sends
   * ITS OWN configured wording for the procedure (or the standard) — a second
   * building's "assemble on the oval" must not become the first's "car park"
   * because the box shows the first. Once the admin has typed, their words go
   * to every building, and the panel says whose wording that replaces.
   */
  const instructionFor = (i: number): string =>
    edited ? instruction : resolveStoredInstruction(procedure, storedQueries[i]?.data?.procedures);
  const ownWordingOf = (i: number): string | null => {
    const row = storedQueries[i]?.data?.procedures.find((x) => x.procedure === procedure);
    return typeof row?.instruction === 'string' && row.instruction.trim() !== ''
      ? row.instruction
      : null;
  };

  // The placeholder while the primary building's preview loads: presence
  // where it is in use, else the roster. Never shown as the answer once the
  // server has one.
  const presence = useQuery({
    queryKey: ['presence', orgId, primaryId],
    queryFn: () => api.getPresence(orgId, primaryId),
    enabled: Boolean(primaryId),
  });
  const roster = useQuery({ queryKey: ['roster', orgId], queryFn: () => api.getRoster(orgId) });

  // The same question the send will ask, per building — answered by the
  // server's audience preview (presence, roster, or the team-scoped roster of
  // migration 0113), never a client-side count.
  const previews = useQueries({
    queries: selected.map((id) => ({
      queryKey: ['audience-preview', orgId, id, mode, scope],
      queryFn: () => api.audiencePreview(orgId, id, mode === 'live' ? 'live' : 'drill', scope),
    })),
  });
  const pointsBySite = useQueries({
    queries: selected.map((id) => ({
      queryKey: ['points', orgId, id],
      queryFn: () => api.getAssemblyPoints(orgId, id),
    })),
  });
  const zonesBySite = useQueries({
    queries: selected.map((id) => ({
      queryKey: ['zones', orgId, id],
      queryFn: () => api.getZones(orgId, id),
    })),
  });
  /** The CAP area description building i is sent: its chosen zones by name, else the whole site. */
  const areaDescFor = (i: number): string => {
    const id = selected[i];
    const zones = zonesBySite[i]?.data?.zones ?? [];
    const chosen = id ? (areas[id] ?? []) : [];
    const names = zones.filter((z) => chosen.includes(z.id)).map((z) => z.name);
    if (names.length === 0) return 'Whole site';
    const joined = names.join(', ');
    // The CAP field is capped at 200 characters; a building with a great many
    // areas chosen is still described, just not exhaustively.
    return joined.length <= 200 ? joined : `${joined.slice(0, 196)}…`;
  };
  const toggleArea = (siteId: string, zoneId: string) =>
    setAreas((prev) => {
      const cur = prev[siteId] ?? [];
      return {
        ...prev,
        [siteId]: cur.includes(zoneId) ? cur.filter((z) => z !== zoneId) : [...cur, zoneId],
      };
    });

  const usingPresence = (presence.data?.counts.total ?? 0) > 0;
  const audienceOf = (i: number): number | null => {
    const count = previews[i]?.data?.count;
    if (typeof count === 'number') return count;
    if (i !== 0) return null;
    return usingPresence ? (presence.data?.counts.total ?? 0) : (roster.data?.counts.active ?? 0);
  };
  const audience = audienceOf(0) ?? 0;
  /** Followers the chosen scope reaches at building i — beside the count, never inside it. */
  const followersOf = (i: number): number => previews[i]?.data?.followers ?? 0;
  const audienceBasis =
    previews[0]?.data?.basis ?? (usingPresence ? ('presence' as const) : ('roster' as const));

  // The building's activation rule (migration 0134). A `two_person` building
  // sends no real alert on one admin, staff-only included — so the one-step
  // wording must not appear while any chosen building is one. The server
  // decides regardless.
  const twoPersonAt = (i: number): boolean =>
    (selectedSites[i]?.activation_rule ?? previews[i]?.data?.activationRule) === 'two_person';
  const twoPersonNames = selectedSites.filter((_, i) => twoPersonAt(i)).map((s) => s.name);
  const twoPersonBuilding = twoPersonNames.length > 0;
  /** Whether building i's alert goes out in one step — the server's own tiering, per building. */
  const narrow = scope === 'site_staff' || scope === 'site_workforce';
  const oneStepAt = (i: number): boolean => mode === 'drill' || (narrow && !twoPersonAt(i));
  const nowCount = selectedSites.filter((_, i) => oneStepAt(i)).length;
  const waitCount = selectedSites.length - nowCount;
  // Every chosen building sends at once, or every one waits — or, with a
  // one-person building beside a two-person one, both. Then the button must
  // say both: "Request authorisation" over an alert that partly goes out now
  // is the inverted form of the one lie this screen exists to avoid.
  const sendsOnOneAdmin = narrow && !twoPersonBuilding;
  const mixed = mode === 'live' && nowCount > 0 && waitCount > 0;
  const cannotSendLive =
    mode === 'live' && twoPersonBuilding && (previews[0]?.data?.adminCount ?? 2) < 2;

  /* ------------------------------------------------------ the points */

  const kind = destinationKind(procedure);
  /** For each selected building: its points of this procedure's kind, and which are open. */
  const destinations = useMemo(
    () =>
      selected.map((id, i) => {
        const all = pointsBySite[i]?.data?.assemblyPoints ?? [];
        const ofKind = kind ? all.filter((p) => p.kind === kind) : [];
        const open = ofKind.filter((p) => !closed.has(p.id));
        return { siteId: id, loaded: Boolean(pointsBySite[i]?.data), points: ofKind, open };
      }),
    [selected, pointsBySite, kind, closed],
  );

  const togglePoint = (siteIndex: number, pointId: string) => {
    const d = destinations[siteIndex];
    if (!d) return;
    setClosed((prev) => {
      const next = new Set(prev);
      if (next.has(pointId)) next.delete(pointId);
      // The last open point cannot close — an evacuation must have somewhere
      // to send people. The row is disabled too; this is the map-tap path.
      else if (d.open.length > 1) next.add(pointId);
      return next;
    });
  };

  // Turning off a point in a building no longer chosen, or for a procedure no
  // longer in force, must not linger into the send.
  const closedForSend = (siteIndex: number): string[] =>
    (destinations[siteIndex]?.points ?? []).filter((p) => closed.has(p.id)).map((p) => p.id);

  /* --------------------------------------------------------- the map */

  const canvas = useMapCanvas();
  /**
   * The operator may pan and zoom, and the camera comes back to the alert's
   * area by itself once they stop (owner, 2026-10-13). The settled region the
   * engine reports after OUR focus is the baseline; a settled region that
   * differs from it — moved by more than a fifth of its span, or zoomed past
   * 1.6× either way — starts the clock, and the focus is re-applied with a new
   * nonce when it runs out. The baseline is retaken after every re-focus, so
   * the panel's own camera moves never read as the operator's.
   */
  const region = useMapRegion();
  const [returnNonce, setReturnNonce] = useState(0);
  const baseline = useRef<{ key: string; region: typeof region } | null>(null);
  const focusKey = useRef('');
  useEffect(() => {
    if (!region) return;
    const key = focusKey.current;
    if (!key) return;
    if (!baseline.current || baseline.current.key !== key) {
      baseline.current = { key, region };
      return;
    }
    if (!wanderedFrom(baseline.current.region!, region)) return;
    const t = setTimeout(() => setReturnNonce((n) => n + 1), RETURN_AFTER_MS);
    return () => clearTimeout(t);
  }, [region]);
  useEffect(() => {
    const points: MapPoint[] = [];
    const circles: MapCircle[] = [];
    const polygons: MapPolygon[] = [];
    const coords: { latitude: number; longitude: number }[] = [];
    selectedSites.forEach((s, i) => {
      if (s.latitude != null && s.longitude != null) {
        coords.push({ latitude: s.latitude, longitude: s.longitude });
        if (s.geofence_radius_m != null) {
          circles.push({
            id: `boundary-${s.id}`,
            latitude: s.latitude,
            longitude: s.longitude,
            radiusM: s.geofence_radius_m,
            kind: 'boundary',
          });
        } else if (s.boundary_polygon && s.boundary_polygon.length >= 3) {
          polygons.push({ id: `outline-${s.id}`, ring: s.boundary_polygon, kind: 'boundary' });
        }
      }
      for (const p of destinations[i]?.points ?? []) {
        const dim = closed.has(p.id);
        coords.push({ latitude: p.latitude, longitude: p.longitude });
        points.push({
          id: p.id,
          latitude: p.latitude,
          longitude: p.longitude,
          label: dim ? `${p.name} — closed` : p.name,
          kind: 'assembly',
          dim,
        });
        if (p.radius_m != null) {
          circles.push({
            id: `area-${p.id}`,
            latitude: p.latitude,
            longitude: p.longitude,
            radiusM: p.radius_m,
            kind: 'assembly',
            dim,
          });
        } else if (p.area_polygon && p.area_polygon.length >= 3) {
          polygons.push({ id: `poly-${p.id}`, ring: p.area_polygon, kind: 'assembly', dim });
        }
      }
    });
    const frame = frameFor(null, coords);
    // The panel covers the map's left edge up to max(760px, 50vw) on desktop —
    // tell the map so the camera centres in the VISIBLE half, and widen the
    // frame by the same fraction so a point west of the building lands in that
    // half rather than under the panel.
    const width = typeof window !== 'undefined' ? window.innerWidth : 0;
    const insetLeftPx = width >= 1024 ? Math.max(760, Math.round(width / 2)) : 0;
    const visible = width > insetLeftPx ? (width - insetLeftPx) / width : 1;
    const focus = frame ? { ...frame, span: frame.span / visible, nonce: returnNonce } : null;
    focusKey.current = focus
      ? `${focus.latitude},${focus.longitude},${focus.span},${focus.nonce}`
      : '';
    // Once buildings are chosen, they and their points are the only things on
    // the map: every other building's pin is hidden (the substrate never hides
    // one that is alerting). With nothing chosen the whole estate shows, so
    // the operator can see what there is to choose from.
    const hideSiteIds =
      selectedSites.length > 0
        ? sites.filter((s) => !selected.includes(s.id)).map((s) => s.id)
        : [];
    canvas.setContent({
      points,
      circles,
      polygons,
      insetLeftPx,
      hideSiteIds,
      ...(focus ? { focus } : {}),
      // A tap on a point's pin turns it off, or back on.
      onSelectPoint: (pointId) => {
        const i = destinations.findIndex((d) => d.points.some((p) => p.id === pointId));
        if (i >= 0) togglePoint(i, pointId);
      },
    });
    // The setters are useCallback-stable (map-layer.tsx).
  }, [canvas.setContent, sites, selected, selectedSites, destinations, closed, returnNonce]);

  /* -------------------------------------------------------- sending */

  const qc = useQueryClient();
  const start = useMutation({
    mutationFn: async (): Promise<Outcome[]> => {
      const out: Outcome[] = [];
      // One event per building, in the order chosen; a refusal on one never
      // stops the next, and every answer is reported on its own.
      for (let i = 0; i < selected.length; i += 1) {
        const id = selected[i]!;
        try {
          const res = await api.raiseEvent(orgId, id, {
            mode,
            procedure,
            instruction: instructionFor(i),
            areaDesc: areaDescFor(i),
            audienceScope: scope,
            ...(closedForSend(i).length > 0 ? { closedAssemblyPointIds: closedForSend(i) } : {}),
          });
          if (!res) {
            out.push({
              siteId: id,
              ok: false,
              message: 'The server gave no answer.',
              runningEventId: null,
            });
            continue;
          }
          out.push({ siteId: id, ok: true, res });
        } catch (e) {
          const blocking = e instanceof api.ApiError ? e.detail.runningEventId : null;
          out.push({
            siteId: id,
            ok: false,
            message: api.errorMessage(e) ?? 'This alert could not be raised.',
            runningEventId: typeof blocking === 'string' ? blocking : null,
          });
        }
      }
      return out;
    },
    /*
      The server decides whether each went out or is waiting for a second
      admin, and the screen reads that rather than predicting it. Predicting it
      would mean two places encoding R1's tiers, and the one that drifted would
      be telling somebody an alert had been sent when it had not.
    */
    onSuccess: (out) => {
      void qc.invalidateQueries({ queryKey: ['events', orgId] });
      if (out.length !== 1) {
        setOutcomes(out);
        return;
      }
      const o = out[0]!;
      if (!o.ok) {
        setError(o.message);
        setBlockedBy(o.runningEventId);
        return;
      }
      const res = o.res;
      if (res.state === 'pending_second_confirm') {
        setAwaiting(res.eventId);
        setAuthorisers(res.authorisers ?? []);
      } else if (res.previewAndRetract && res.retractDeadlineAt) {
        // Tier-2 single-admin: the alert is already out and the R3 safety is
        // ticking. Hold the panel rather than dismissing to the live view —
        // the admin should never have to leave this screen to hit Retract.
        setRetracting({
          eventId: res.eventId,
          deadlineIso: res.retractDeadlineAt,
          audienceLabel: audienceInWords(mode, scope, audience, primary?.name ?? 'this site'),
        });
      } else {
        onLive(res.eventId);
      }
    },
    onError: (e: Error) => setError(e.message),
  });

  const cancel = useMutation({
    mutationFn: (eventId: string) => api.cancelEvent(orgId, eventId),
    onSuccess: () => {
      setAwaiting(null);
      setError(null);
    },
    onError: (e: Error) => setError(e.message),
  });

  const retractFromDialog = useMutation({
    mutationFn: (eventId: string) => api.retractEvent(orgId, eventId),
    onSuccess: (_, eventId) => {
      void qc.invalidateQueries({ queryKey: ['live-readiness', orgId] });
      void qc.invalidateQueries({ queryKey: ['event', orgId, eventId] });
      setRetracting(null);
      onLive(eventId);
    },
    onError: (e: Error) => setError(e.message),
  });

  const toggleSite = (id: string) =>
    setSelected((prev) => {
      if (prev.includes(id)) return prev.length > 1 ? prev.filter((x) => x !== id) : prev;
      return [...prev, id];
    });

  const sent = outcomes !== null;
  const canSend =
    selected.length > 0 &&
    !start.isPending &&
    instruction.trim() !== '' &&
    !storedQueries.some((q) => q.isLoading) &&
    !cannotSendLive &&
    !sent;

  const alertSentence = (() => {
    const lead = mode === 'drill' ? 'This drill will alert ' : 'This will alert ';
    const parts = selectedSites.map((s, i) => {
      const n = audienceOf(i);
      const count =
        n === null ? (
          <span key={`c-${s.id}`}>counting…</span>
        ) : (
          <span key={`c-${s.id}`}>
            <strong className="tabular">{n}</strong> {n === 1 ? 'person' : 'people'}
          </span>
        );
      const following = followersOf(i);
      return (
        <span key={s.id}>
          {i > 0 ? (i === selectedSites.length - 1 ? ' and ' : ', ') : ''}
          {count} at <strong>{s.name}</strong>
          {following > 0 ? (
            <>
              , and <strong className="tabular">{following}</strong>{' '}
              {following === 1 ? 'person' : 'people'} following
            </>
          ) : (
            ''
          )}
          {mixed ? (oneStepAt(i) ? ' now' : ' once a second admin authorises it') : ''}
        </span>
      );
    });
    return (
      <>
        {lead}
        {parts.length > 0 ? parts : <>nobody yet — choose a building</>}
        {mode === 'live' && !mixed && !sendsOnOneAdmin ? ', once a second admin authorises it' : ''}
        .
      </>
    );
  })();

  const kindLabel =
    kind === 'outdoor_assembly'
      ? 'Evacuation points'
      : kind === 'indoor_shelter'
        ? 'Lockdown points'
        : null;

  const inner = (
    <div className="mx-auto max-w-[520px]">
      <div className="flex items-start justify-between gap-4">
        {/* The rail's register, not the sign-in's (owner, 2026-10-14: "the
            font size of the left hand menu"): 14px values, 11px labels, a
            heading one step up. No subtitle — the first control says drill
            or real, which is all a subtitle used to say. */}
        <h1 className="m-0 text-[20px] leading-[24px] font-[650] tracking-[-0.01em] text-[var(--ink)]">
          Raise an alert
        </h1>
        {onClose ? (
          // The round grey ✕ every Apple sheet closes with. Named for assistive
          // technology, so it is a glyph to the eye and a word to a reader.
          <button
            type="button"
            onClick={onClose}
            disabled={start.isPending}
            aria-label={sent ? 'Leave the raise panel' : 'Close the raise panel'}
            className="flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-full bg-[var(--paper-3)] text-[var(--ink-2)] hover:text-[var(--ink)] disabled:opacity-50"
          >
            <svg aria-hidden width="10" height="10" viewBox="0 0 12 12" fill="none">
              <path
                d="M2 2l8 8M10 2l-8 8"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
              />
            </svg>
          </button>
        ) : null}
      </div>

      {/*
        One form, the Apple Business shape: every choice is a 56px field or a
        segmented control, stacked at one rhythm, with a line of help only
        where it says something the label does not. The explanations that
        used to sit under every control are gone — the field's value is the
        explanation — and the one consequence that changes who must press
        send (R1 §1.1) stays beside the control that causes it.
      */}
      <div className="mt-5 space-y-3">
        {/*
          Drill or real, chosen before the words are written rather than
          after, because it changes who is told and what the message says
          about itself. Radios, not a select: the one choice on the screen
          that must never be made by accident.
        */}
        <Segmented
          name="mode"
          legend="Alert kind"
          value={mode}
          disabled={sent}
          onChange={(v) => setMode(v)}
          options={[
            { value: 'drill', label: 'Drill' },
            { value: 'live', label: 'Real emergency', danger: true },
          ]}
        />

        <div>
          <Segmented
            name="procedure"
            legend="Procedure"
            value={procedure}
            disabled={sent}
            onChange={(v) => setProcedure(v)}
            options={PROCEDURE_CARDS.map((c) => ({
              value: c.value,
              label: c.label,
              glyph: c.glyph(14),
            }))}
          />
          {procedure === 'lockdown' ? (
            <Help>
              A lockdown is delivered silently — movement is the danger, and a sound can give
              somebody away.
            </Help>
          ) : null}
        </div>

        {sites.length === 0 ? (
          <Help>This organisation has no building yet, so there is nowhere to raise an alert.</Help>
        ) : (
          <BuildingPicker
            sites={sites}
            selected={selected}
            disabled={sent}
            countOf={(id) => {
              const i = selected.indexOf(id);
              return i >= 0 ? audienceOf(i) : null;
            }}
            onToggle={toggleSite}
          />
        )}

        <div>
          <FieldSelect
            label="Who is told"
            value={scope}
            disabled={sent}
            onChange={(v) => setScope(v as api.AudienceScope)}
            options={[
              { value: 'site_all', label: 'All staff, everyone on site, and everyone following' },
              { value: 'site_staff', label: 'Everyone on site only' },
              { value: 'site_workforce', label: 'Staff only' },
            ]}
          />
          {/*
            How far it reaches is what decides whether a second admin is
            needed (R1 §1.1) — so the consequence is stated beside the
            control that causes it, not discovered after pressing send.
          */}
          {mode === 'live' ? (
            <Help>
              {cannotSendLive
                ? 'No real alert can be sent from here: this building needs a second administrator to authorise any real alert, and this organisation has only one. Call 000 and use the building’s PA. To fix: add a second administrator.'
                : twoPersonBuilding
                  ? selectedSites.length > 1
                    ? `${twoPersonNames.join(' and ')} ${twoPersonNames.length > 1 ? 'need' : 'needs'} a second administrator to authorise any real alert, so nothing is sent there until one does.`
                    : 'This building needs a second administrator to authorise any real alert, so nothing is sent until one does.'
                  : narrow
                    ? 'The people already in the building can check the claim with their own eyes, so this goes out on one admin.'
                    : 'This reaches past the people who can see for themselves, so a second admin has to authorise it before anything is sent.'}
            </Help>
          ) : (
            <Help>A drill reaches followers only if they asked to hear about drills.</Help>
          )}
        </div>

        {/*
          The area the message is about: the whole building unless zones are
          picked, per building. "Whole site" is the first row and the default;
          ticking it again clears the zones.
        */}
        {selectedSites.map((site, i) => {
          const zones = zonesBySite[i]?.data?.zones ?? [];
          const chosen = areas[site.id] ?? [];
          const label = selectedSites.length > 1 ? `Area · ${site.name}` : 'Area';
          if (zones.length === 0) {
            return (
              <div key={site.id}>
                <FieldStatic label={label} muted={!zonesBySite[i]?.data}>
                  Whole site
                </FieldStatic>
                {zonesBySite[i]?.data ? (
                  <Help>This building has no areas set up, so the alert is about all of it.</Help>
                ) : null}
              </div>
            );
          }
          return (
            <MultiPicker
              key={site.id}
              label={label}
              items={[
                {
                  id: WHOLE_SITE,
                  label: 'Whole site',
                  checkboxLabel: `Whole site at ${site.name}`,
                },
                ...zones.map((z) => ({
                  id: z.id,
                  label: z.name,
                  checkboxLabel: `${z.name} at ${site.name}`,
                })),
              ]}
              selected={chosen.length === 0 ? [WHOLE_SITE] : chosen}
              disabled={sent}
              placeholder="Whole site"
              groupLabel={`Areas of ${site.name}`}
              listId={`raise-area-list-${site.id}`}
              onToggle={(id) =>
                id === WHOLE_SITE
                  ? setAreas((prev) => ({ ...prev, [site.id]: [] }))
                  : toggleArea(site.id, id)
              }
            />
          );
        })}

        {/*
          Where people go: every point of the procedure's kind, each with a
          switch. No primary — people go to any open point — and the last
          open one cannot be turned off (the server refuses it too).
        */}
        {kindLabel && selectedSites.length > 0 ? (
          <div>
            <div className="rounded-[12px] border border-[var(--line-2)] bg-[var(--paper)]/80">
              <p className="m-0 px-3 pt-2 pb-0.5 text-[11px] leading-[14px] text-[var(--ink-3)]">
                {kindLabel}
              </p>
              <ul className="m-0 list-none divide-y divide-[var(--line)] p-0">
                {destinations.map((d, i) => {
                  const site = selectedSites[i];
                  if (!site) return null;
                  const prefix = selectedSites.length > 1 ? `${site.name} · ` : '';
                  if (!d.loaded) {
                    return (
                      <li key={d.siteId} className="px-3 py-2 text-[14px] text-[var(--ink-2)]">
                        {prefix}Loading its points…
                      </li>
                    );
                  }
                  if (d.points.length === 0) {
                    return (
                      <li key={d.siteId} className="px-3 py-2 text-[14px] text-[var(--ink-2)]">
                        {prefix}None set — people get the instruction only.
                        {onOpenBuilding ? (
                          <>
                            {' '}
                            <InlineAction onClick={() => onOpenBuilding(site.id)}>
                              Add one on {site.name}
                            </InlineAction>
                          </>
                        ) : null}
                      </li>
                    );
                  }
                  return d.points.map((p) => {
                    const isClosed = closed.has(p.id);
                    const lastOpen = !isClosed && d.open.length <= 1;
                    return (
                      <li key={p.id}>
                        <Switch
                          checked={!isClosed}
                          disabled={sent || lastOpen}
                          onChange={() => togglePoint(i, p.id)}
                          ariaLabel={`${p.name} open for this alert`}
                          describedBy={`point-state-${p.id}`}
                        >
                          <span
                            className={`block truncate text-[14px] leading-[18px] ${isClosed ? 'text-[var(--ink-2)] line-through' : ''}`}
                          >
                            {prefix}
                            {p.name}
                          </span>
                          <span
                            id={`point-state-${p.id}`}
                            className="block text-[12px] leading-[15px] text-[var(--ink-2)]"
                          >
                            {isClosed
                              ? 'Closed for this alert'
                              : lastOpen
                                ? 'Only open point — stays open'
                                : 'Open'}
                          </span>
                        </Switch>
                      </li>
                    );
                  });
                })}
              </ul>
            </div>
            <Help>
              {kind === 'outdoor_assembly'
                ? 'People can go to any open point. Turn one off here or by tapping its pin.'
                : 'People shelter at any open point. Turn one off here or by tapping its pin.'}
            </Help>
          </div>
        ) : null}

        <div>
          <FieldInput
            label="Instruction"
            value={instruction}
            disabled={sent}
            onChange={(v) => {
              setEdited(true);
              setInstruction(v);
            }}
          />
          {/*
            Where these words came from, never left silent. An admin must be
            able to tell whether the box holds THIS building's configured
            wording or the generic standard, and must be told when the
            configured wording could not be loaded rather than shown the
            standard as if it were the building's own.
          */}
          {instruction.trim() === '' ? (
            <Help tone="attention">Write what people need to know before sending.</Help>
          ) : null}
          {storedProcedures?.isError ? (
            <Help tone="attention">
              Couldn&rsquo;t load this building&rsquo;s configured wording — showing the standard
              wording.{' '}
              <InlineAction onClick={() => void storedProcedures?.refetch()}>Retry</InlineAction>
            </Help>
          ) : storedProcedures?.isLoading ? (
            <Help>Loading this building&rsquo;s wording…</Help>
          ) : (
            <Help>
              {edited
                ? 'Your own wording for this alert.'
                : hasStored
                  ? `${primary?.name ?? 'This building'}’s configured wording.`
                  : 'Standard wording — this building has none set.'}
            </Help>
          )}
          {selectedSites.length > 1 ? (
            <OtherBuildingsWording
              sites={selectedSites}
              edited={edited}
              ownWordingOf={ownWordingOf}
            />
          ) : null}
        </div>
      </div>

      <div className="mt-7">
        <p className="m-0 text-[15px] leading-[20px]">{alertSentence}</p>
        <p className="mt-1 mb-4 text-[12px] leading-[16px] text-[var(--ink-2)]">
          {audienceBasis === 'presence'
            ? 'Counted from who is signed in as present, including visitors.'
            : audienceBasis === 'team_roster'
              ? 'Counted from the teams linked to this building, plus its wardens and administrators — people outside those teams are not alerted here.'
              : 'Counted from your people list — nobody is signed in as present, so visitors are not included.'}
          {mode === 'drill'
            ? ' Every message says it is a drill, so nobody has to work out whether it is one.'
            : ''}
        </p>

        <Button
          variant="primary"
          size="live"
          full
          danger={mode === 'live'}
          /*
            No send with a blank instruction. A 'communication' procedure has
            an empty built-in default, so without this it could go out saying
            nothing. Also held while the building's stored wording is still
            loading, so the send cannot beat its own configured words to the box.
          */
          disabled={!canSend}
          onClick={() => start.mutate()}
        >
          {start.isPending
            ? 'Sending…'
            : mode === 'drill'
              ? selectedSites.length > 1
                ? `Start ${selectedSites.length} drills`
                : 'Start drill'
              : mixed
                ? `Alert ${nowCount} now, request authorisation for ${waitCount}`
                : sendsOnOneAdmin
                  ? 'Alert them now'
                  : 'Request authorisation'}
        </Button>

        {/*
          Raised and waiting. The screen must not imply anything has been sent
          — nothing has — and the way to withdraw it has to be at least as easy
          as raising it was, on the same reasoning that makes a correction
          single-admin (migration 0021).
        */}
        {awaiting ? (
          <div className="mt-6">
            <Finding tone="attention">
              Raised, and nothing has been sent. A second admin has to authorise it — it lapses on
              its own if nobody does, because two people eventually is one person with extra steps.
            </Finding>
            <p className="mt-2 text-[14px] text-[var(--ink-2)]">
              {authorisers.length > 0
                ? `Sent to ${authorisers.join(', ')} for authorisation.`
                : 'There is no other administrator to send it to. Call 000 and use the building’s PA.'}
            </p>
            <div className="mt-3 flex flex-wrap gap-3">
              <Button onClick={() => onLive(awaiting)}>Open it</Button>
              <Button
                variant="quiet"
                onClick={() => {
                  cancel.mutate(awaiting);
                }}
                disabled={cancel.isPending}
              >
                Withdraw it
              </Button>
            </div>
          </div>
        ) : null}

        {retracting ? (
          <RetractDialog
            audienceLabel={retracting.audienceLabel}
            deadlineIso={retracting.deadlineIso}
            pending={retractFromDialog.isPending}
            onRetract={() => retractFromDialog.mutate(retracting.eventId)}
            onDismiss={() => {
              const eventId = retracting.eventId;
              setRetracting(null);
              onLive(eventId);
            }}
          />
        ) : null}

        {outcomes ? (
          <Outcomes orgId={orgId} outcomes={outcomes} sites={sites} mode={mode} onLive={onLive} />
        ) : null}

        {error ? (
          <div className="mt-4">
            <Finding>
              {error}
              {blockedBy ? (
                <>
                  {' '}
                  <InlineAction onClick={() => onLive(blockedBy)}>
                    Open the one running
                  </InlineAction>
                </>
              ) : null}
            </Finding>
          </div>
        ) : null}
      </div>
    </div>
  );

  // A glass panel sliding out from the rail over the LEFT half of the screen,
  // with the app's own persistent map — fed by the effect above — living on
  // the right. The Shell renders this route `bleed` (main is
  // pointer-events-none, like Home), so the panel opts back in and every map
  // gesture on the right falls through to the substrate. Anchored absolutely
  // inside <main>, which already carries the rail offset and starts under the
  // top bar and banner.
  return (
    <div className="panel-in-left pointer-events-auto absolute inset-y-0 left-0 z-20 w-full lg:w-[max(560px,calc(50vw-240px))]">
      <div className="sheet h-full overflow-y-auto border-t border-r border-[var(--glass-edge)] p-[var(--sheet-pad)]">
        {inner}
      </div>
    </div>
  );
}

/**
 * What came back when more than one building was raised — one row per
 * building, each reading the server's own answer: sent, waiting for a second
 * admin, out with the retract window ticking, or refused in words.
 */
function Outcomes({
  orgId,
  outcomes,
  sites,
  mode,
  onLive,
}: {
  orgId: string;
  outcomes: Outcome[];
  sites: api.Site[];
  mode: 'drill' | 'live';
  onLive: (eventId: string) => void;
}) {
  const nameOf = (id: string) => sites.find((s) => s.id === id)?.name ?? 'This building';
  return (
    <div className="mt-6 max-w-[560px]">
      <p className="mb-2 text-[12px] font-medium tracking-[0.04em] text-[var(--ink-2)] uppercase">
        What happened
      </p>
      <ul className="m-0 list-none divide-y divide-[var(--line)] p-0">
        {outcomes.map((o) => (
          <li key={o.siteId} className="py-3">
            <p className="text-[15px] font-medium">{nameOf(o.siteId)}</p>
            {!o.ok ? (
              <p className="mt-1 text-[14px] text-[var(--alert)]">
                Not sent — {o.message}
                {o.runningEventId ? (
                  <>
                    {' '}
                    <InlineAction onClick={() => onLive(o.runningEventId!)}>
                      Open the one running
                    </InlineAction>
                  </>
                ) : null}
              </p>
            ) : o.res.state === 'pending_second_confirm' ? (
              <PendingRow
                orgId={orgId}
                eventId={o.res.eventId}
                authorisers={o.res.authorisers ?? []}
                onLive={onLive}
              />
            ) : o.res.previewAndRetract && o.res.retractDeadlineAt ? (
              <RetractRow
                orgId={orgId}
                eventId={o.res.eventId}
                deadlineIso={o.res.retractDeadlineAt}
                onLive={onLive}
              />
            ) : (
              <p className="mt-1 text-[14px] text-[var(--ink-2)]">
                {mode === 'drill' ? 'Drill started' : 'Alert sent'} —{' '}
                <strong className="tabular text-[var(--ink)]">{o.res.recipients}</strong> told.{' '}
                <InlineAction onClick={() => onLive(o.res.eventId)}>
                  Open the live view
                </InlineAction>
              </p>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * A field that opens a tick-list, not a list on the page.
 *
 * Reads as one line ("Lincoln Primary and 2 more") and opens only when the
 * operator wants to change it; closes like a menu — a click anywhere off it,
 * or Escape — with no Done button. Shared by the buildings field and the
 * per-building areas field.
 */
function MultiPicker({
  label,
  items,
  selected,
  disabled,
  placeholder,
  groupLabel,
  listId,
  onToggle,
}: {
  /** The field's floating label. */
  label: string;
  items: {
    id: string;
    label: string;
    detail?: string | null;
    trailing?: ReactNode;
    checkboxLabel: string;
  }[];
  selected: string[];
  disabled: boolean;
  /** What the field reads with nothing chosen. */
  placeholder: string;
  groupLabel: string;
  listId: string;
  onToggle: (id: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const names = selected
    .map((id) => items.find((s) => s.id === id)?.label)
    .filter((n): n is string => Boolean(n));
  const summary =
    names.length === 0
      ? placeholder
      : names.length <= 2
        ? names.join(' and ')
        : `${names[0]} and ${names.length - 1} more`;
  const root = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const onPress = (e: MouseEvent) => {
      if (root.current && !root.current.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onPress);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onPress);
    };
  }, [open]);
  return (
    <div ref={root} className="relative">
      <button
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        /* The floating label and the value both sit inside the button, but
           the name a reader hears has to say which field this is first. */
        aria-label={`${label}: ${summary}`}
        onClick={() => setOpen((v) => !v)}
        className={`${FIELD} text-left disabled:opacity-60`}
      >
        <FieldLabel>{label}</FieldLabel>
        <span className={`block truncate ${names.length === 0 ? 'text-[var(--ink-2)]' : ''}`}>
          {summary}
        </span>
        <Chevron />
      </button>
      {open ? (
        <ul
          id={listId}
          role="group"
          aria-label={groupLabel}
          className="sheet elev-2 absolute left-0 right-0 z-30 mt-1 max-h-[320px] overflow-y-auto rounded-[10px] border border-[var(--glass-edge)] p-1"
        >
          {items.map((it) => {
            const on = selected.includes(it.id);
            return (
              <li key={it.id}>
                <label className="flex cursor-pointer items-center gap-3 rounded-[8px] px-2 py-2 hover:bg-[var(--paper-3)]">
                  <input
                    type="checkbox"
                    checked={on}
                    disabled={disabled}
                    onChange={() => onToggle(it.id)}
                    aria-label={it.checkboxLabel}
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block text-[14px] font-medium">{it.label}</span>
                    {it.detail ? (
                      <span className="block truncate text-[12px] text-[var(--ink-2)]">
                        {it.detail}
                      </span>
                    ) : null}
                  </span>
                  {on && it.trailing ? (
                    <span className="shrink-0 text-[12px] text-[var(--ink-2)]">{it.trailing}</span>
                  ) : null}
                </label>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}

/**
 * Which buildings. With several, nothing is pre-chosen, so the operator has
 * to open the list and pick — the one deliberate act the whole panel turns
 * on. With one, it is simply named.
 */
function BuildingPicker({
  sites,
  selected,
  disabled,
  countOf,
  onToggle,
}: {
  sites: api.Site[];
  selected: string[];
  disabled: boolean;
  countOf: (siteId: string) => number | null;
  onToggle: (siteId: string) => void;
}) {
  const count = (id: string): ReactNode => {
    const n = countOf(id);
    return n === null ? (
      'counting…'
    ) : (
      <>
        <strong className="tabular text-[var(--ink)]">{n}</strong> {n === 1 ? 'person' : 'people'}
      </>
    );
  };
  if (sites.length === 1) {
    const only = sites[0]!;
    return (
      <FieldStatic label="Building">
        {only.name}
        {only.address ? <span className="text-[var(--ink-2)]"> · {only.address}</span> : null}
      </FieldStatic>
    );
  }
  return (
    <MultiPicker
      label="Buildings"
      items={sites.map((s) => ({
        id: s.id,
        label: s.name,
        detail: s.address,
        trailing: count(s.id),
        checkboxLabel: `Raise at ${s.name}`,
      }))}
      selected={selected}
      disabled={disabled}
      placeholder="Choose a building…"
      groupLabel="Buildings"
      listId="raise-building-list"
      onToggle={onToggle}
    />
  );
}

/**
 * What the other chosen buildings will be told, beside the box that shows the
 * first. Without this a second building's own configured wording is replaced
 * silently, and a wrong route sends people somewhere they cannot go.
 */
function OtherBuildingsWording({
  sites,
  edited,
  ownWordingOf,
}: {
  sites: api.Site[];
  edited: boolean;
  ownWordingOf: (i: number) => string | null;
}) {
  const others = sites.map((s, i) => ({ site: s, own: ownWordingOf(i) })).slice(1);
  const withOwn = others.filter((o) => o.own !== null);
  if (edited) {
    if (withOwn.length === 0) {
      return <Help>Your words go to every building chosen.</Help>;
    }
    return (
      <div className="mt-2">
        <Finding tone="attention">
          {withOwn.map((o) => o.site.name).join(' and ')}{' '}
          {withOwn.length > 1 ? 'have their' : 'has its'} own wording for this procedure — your
          words replace it.
        </Finding>
      </div>
    );
  }
  return (
    <ul className="m-0 mt-1 list-none space-y-0.5 px-0.5 text-[12px] leading-[16px] text-[var(--ink-2)]">
      {others.map((o) => (
        <li key={o.site.id}>
          <span className="font-medium text-[var(--ink)]">{o.site.name}</span>
          {o.own ? ` is told its own wording: “${o.own}”` : ' is told the standard wording.'}
        </li>
      ))}
    </ul>
  );
}

/** One building's alert, raised and waiting for a second admin — with the way to withdraw it. */
function PendingRow({
  orgId,
  eventId,
  authorisers,
  onLive,
}: {
  orgId: string;
  eventId: string;
  authorisers: string[];
  onLive: (eventId: string) => void;
}) {
  const cancel = useMutation({ mutationFn: () => api.cancelEvent(orgId, eventId) });
  if (cancel.isSuccess) {
    return <p className="mt-1 text-[14px] text-[var(--ink-2)]">Withdrawn — nothing was sent.</p>;
  }
  return (
    <p className="mt-1 text-[14px] text-[var(--ink-2)]">
      Waiting for a second admin to authorise it
      {authorisers.length > 0 ? ` — sent to ${authorisers.join(', ')}` : ''}.{' '}
      <InlineAction onClick={() => onLive(eventId)}>Open it</InlineAction>{' '}
      <InlineAction onClick={() => cancel.mutate()}>
        {cancel.isPending ? 'Withdrawing…' : 'Withdraw it'}
      </InlineAction>
      {cancel.isError ? (
        <span className="text-[var(--alert)]"> {api.errorMessage(cancel.error)}</span>
      ) : null}
    </p>
  );
}

/** One building's alert, already out, with its one-tap retract window ticking. */
function RetractRow({
  orgId,
  eventId,
  deadlineIso,
  onLive,
}: {
  orgId: string;
  eventId: string;
  deadlineIso: string;
  onLive: (eventId: string) => void;
}) {
  const remaining = useCountdown(deadlineIso);
  const retract = useMutation({
    mutationFn: () => api.retractEvent(orgId, eventId),
    onSuccess: () => onLive(eventId),
  });
  return (
    <p className="mt-1 text-[14px] text-[var(--ink-2)]">
      Alert sent.{' '}
      {remaining !== null ? (
        <>
          <span role="status" aria-live="polite" aria-atomic="true">
            <strong className="tabular text-[var(--ink)]">{remaining}s</strong> to retract.
          </span>{' '}
          <InlineAction onClick={() => retract.mutate()}>
            {retract.isPending ? 'Retracting…' : 'Retract'}
          </InlineAction>{' '}
        </>
      ) : null}
      <InlineAction onClick={() => onLive(eventId)}>Open the live view</InlineAction>
      {retract.isError ? (
        <span className="text-[var(--alert)]"> {api.errorMessage(retract.error)}</span>
      ) : null}
    </p>
  );
}

/**
 * "Alerting the people on site" — the audience the send just went to, stated
 * in words so the intermediate raise-dialog state can name it back to the
 * operator. Reuses the exact phrasing the Alert them / Request authorisation
 * control uses, because a mismatch here is the class of bug that ends up
 * saying "everyone at X is safe" when nobody has been asked.
 */
function audienceInWords(
  mode: 'drill' | 'live',
  scope: api.AudienceScope,
  audience: number,
  siteName: string,
): string {
  const count = `${audience} ${audience === 1 ? 'person' : 'people'}`;
  if (mode === 'drill') return `${count} at ${siteName}`;
  switch (scope) {
    case 'site_workforce':
      return `the on-site workforce at ${siteName} (${count})`;
    case 'site_staff':
      return `the people on site at ${siteName} (${count})`;
    case 'site_all':
      return `everyone at ${siteName} and those following it (${count})`;
    case 'cross_org':
      return `${count} beyond this organisation`;
  }
}

/**
 * The intermediate raise-dialog state on the tier-2 preview-and-retract
 * path: the alert is already out, and the R3 safety window is ticking.
 *
 * Two buttons and a live countdown. The countdown is a `role="status"`
 * live region, deliberately not `role="alert"` — the number ticking is a
 * passive value, not news; assertive live regions interrupt screen readers
 * every second, which is worse than silence for a countdown.
 *
 * Retract is the primary control here. Its label carries the countdown
 * ("Retract · 47s") because a label that only reads "Retract" gives no clue
 * how long it stays available — and after the deadline the button
 * disappears rather than becoming a subtly-different "correction" without
 * saying so.
 */
function RetractDialog({
  audienceLabel,
  deadlineIso,
  pending,
  onRetract,
  onDismiss,
}: {
  audienceLabel: string;
  deadlineIso: string;
  pending: boolean;
  onRetract: () => void;
  onDismiss: () => void;
}) {
  const remaining = useCountdown(deadlineIso);

  return (
    <div className="mt-6 max-w-[560px]">
      <Finding tone="alert">
        <span className="block">
          Alerting <strong>{audienceLabel}</strong>.
        </span>
        {remaining !== null ? (
          <span
            role="status"
            aria-live="polite"
            aria-atomic="true"
            className="mt-2 block text-[14px] text-[var(--ink-2)]"
          >
            You have <strong className="tabular">{remaining}s</strong> to retract if this should not
            have gone out.
          </span>
        ) : (
          <span className="mt-2 block text-[14px] text-[var(--ink-2)]">
            The one-tap retract window has passed. A retraction is still available on the live view
            as an ordinary correction.
          </span>
        )}
      </Finding>
      <div className="mt-3 flex flex-wrap gap-3">
        {remaining !== null ? (
          <Button
            variant="primary"
            danger
            disabled={pending}
            onClick={onRetract}
            label={`Retract this alert — ${remaining}s left in the one-tap window`}
          >
            {pending ? 'Retracting…' : `Retract · ${remaining}s`}
          </Button>
        ) : null}
        <Button variant="quiet" onClick={onDismiss}>
          {remaining !== null ? 'Open the live view' : 'Continue to the live view'}
        </Button>
      </div>
    </div>
  );
}
